/**
 * MediaKitSection — Press / Media Kit for Sean The Science Kid.
 * Source: Sean's official 8-page media kit (OneDrive doc, June 2026).
 *
 * Structured as a print-friendly 4-page mini-kit: Cover → Meet Sean → Audience & Reach
 * → Media Features. Pages 5-8 of the source doc didn't load via the viewer; can be
 * grafted in once available.
 */
import React from "react";

const REACH = [
  { platform: "Instagram", count: "1.5M+",  emoji: "📸", handle: "@seanthesciencekidofficial" },
  { platform: "Facebook",  count: "383K+",  emoji: "👍", handle: "Sean The Science Kid" },
  { platform: "Threads",   count: "74K+",   emoji: "🧵", handle: "@seanthesciencekidofficial" },
  { platform: "TikTok",    count: "35K+",   emoji: "🎵", handle: "@seanthesciencekid" },
  { platform: "YouTube",   count: "26K+",   emoji: "📺", handle: "Sean The Science Kid" },
];

const MARKETS = ["United States", "United Kingdom", "Canada", "Ghana", "Nigeria", "South Africa", "Philippines"];

const FEATURES = [
  {
    outlet: "ABC News & Good Morning America",
    blurb: "Sean was featured nationally for his work teaching science through social media and inspiring young learners worldwide.",
    tier: "national",
  },
  {
    outlet: "Adam Grant's ReThinking",
    blurb: "Featured on the bestselling podcast hosted by organizational psychologist and Wharton professor Adam Grant.",
    tier: "podcast",
  },
  {
    outlet: "TED Audio Collective",
    blurb: "Youngest contributor in the TED Audio Collective ecosystem — bringing science communication to a global stage.",
    tier: "global",
  },
  {
    outlet: "People Magazine",
    blurb: "Profile on rising young STEM educators reshaping science communication for the next generation.",
    tier: "national",
  },
];

const GROWTH_STATS = [
  { label: "New Facebook followers daily", value: "694" },
  { label: "Monthly follower growth",      value: "20,000+" },
  { label: "Daily page views",             value: "4,400+" },
  { label: "Weekly posts",                 value: "21+" },
];

const ROLES = ["Future Neuro-Cardio Surgeon", "STEM Educator", "Science Communicator", "Youth Speaker"];

export default function MediaKitSection() {
  return (
    <section id="press" className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-10 mt-12 sm:mt-16 scroll-mt-24" data-testid="media-kit-section">
      {/* === PAGE 1: COVER === */}
      <article className="card-flat bg-white mb-6" style={{ border: "3px solid var(--ink)", boxShadow: "5px 5px 0 0 var(--ink)", padding: 0, overflow: "hidden" }} data-testid="press-page-cover">
        <div className="grid md:grid-cols-5 gap-0">
          {/* Left — wordmark + roles */}
          <div className="md:col-span-3 px-5 sm:px-8 py-7 sm:py-10" style={{ background: "linear-gradient(160deg, #ffd93d 0%, #ff8a3d 100%)", borderRight: "3px solid var(--ink)" }}>
            <div className="eyebrow text-[var(--ink)] mb-3" data-testid="press-cover-eyebrow">PRESS &middot; MEDIA KIT &middot; 2026</div>
            <h2 className="font-display text-[var(--ink)] leading-[0.88]" style={{ fontSize: "clamp(2.4rem, 6vw, 4.4rem)" }}>
              SEAN
              <br/>
              <span style={{ color: "var(--red)" }}>THE SCIENCE KID</span>
            </h2>
            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="press-cover-roles">
              {ROLES.map((r) => (
                <span key={r} className="pill" style={{ background: "var(--ink)", color: "#fff", fontSize: "0.7rem" }}>{r}</span>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="pill pill-red" data-testid="press-reach-pill">🌍 2M+ Social Reach</span>
              <span className="pill pill-green">🎓 TED Speaker</span>
              <span className="pill" style={{ background: "var(--cyan)", color: "var(--ink)" }}>🧠 Mensa @ 4</span>
            </div>

            <div className="mt-6 pt-5" style={{ borderTop: "2.5px dashed rgba(14,18,48,0.35)" }}>
              <div className="eyebrow text-[var(--ink)] mb-2">FEATURED BY</div>
              <div className="font-display text-base sm:text-lg text-[var(--ink)] leading-tight">
                ABC News &middot; Good Morning America &middot; TED Audio Collective &middot; Adam Grant's ReThinking &middot; People Magazine
              </div>
            </div>
          </div>

          {/* Right — headshot */}
          <div className="md:col-span-2 relative" style={{ background: "var(--ink)", minHeight: "260px" }}>
            <img
              src="/sean-portrait-new.jpg"
              alt="Sean Atitsogbe — Sean The Science Kid"
              className="w-full h-full block"
              style={{ objectFit: "cover", objectPosition: "center 25%" }}
              data-testid="press-cover-headshot"
            />
            <div className="absolute bottom-0 left-0 right-0 px-4 py-3" style={{ background: "linear-gradient(180deg, rgba(14,18,48,0) 0%, rgba(14,18,48,0.85) 70%)" }}>
              <div className="font-display text-white text-lg leading-tight">Sean Atitsogbe, age 11</div>
              <div className="label-tag" style={{ color: "rgba(255,255,255,0.7)" }}>// official press headshot</div>
            </div>
          </div>
        </div>
      </article>

      {/* === PAGE 2: MEET SEAN === */}
      <article className="card-flat bg-white mb-6" style={{ border: "3px solid var(--ink)", boxShadow: "5px 5px 0 0 var(--ink)" }} data-testid="press-page-bio">
        <div className="grid lg:grid-cols-5 gap-5 lg:gap-8">
          <div className="lg:col-span-3">
            <div className="eyebrow text-[var(--orange)] mb-2">PAGE 02 &middot; MEET SEAN</div>
            <h3 className="font-display text-2xl sm:text-3xl text-[var(--ink)] mb-4 leading-tight">An 11-year-old reshaping science for kids worldwide</h3>
            <p className="text-[var(--ink)] text-base leading-relaxed mb-3">
              <strong>Sean Atitsogbe</strong> is an 11-year-old STEM educator, science communicator, and content creator inspiring children around the world to embrace curiosity and discover the wonder of science.
            </p>
            <p className="text-[var(--ink-soft)] text-sm leading-relaxed">
              Known as <strong className="text-[var(--ink)]">"Sean The Science Kid,"</strong> he simplifies complex scientific concepts through engaging videos, experiments, interviews, and educational storytelling that reach <strong className="text-[var(--ink)]">millions of families globally</strong>. Sean started reading at 9 months old, joined Mensa at age 4, and now uses his platform to push back against misinformation through his Neurocardio Loop framework.
            </p>
          </div>
          <aside className="lg:col-span-2">
            <div className="card-flat tint-orange" data-testid="press-mission">
              <div className="eyebrow text-[var(--red)] mb-1">MISSION</div>
              <div className="font-display text-xl sm:text-2xl text-[var(--ink)] leading-snug">
                Making science fun, exciting, and accessible for every child.
              </div>
            </div>
            <div className="card-flat bg-white mt-3" style={{ background: "var(--paper-2)" }}>
              <div className="eyebrow text-[var(--orange)] mb-2">QUICK FACTS</div>
              <ul className="text-sm text-[var(--ink)] space-y-1.5 leading-relaxed">
                <li>🧠 Joined Mensa at age 4</li>
                <li>📖 Started reading at 9 months</li>
                <li>🎤 TED speaker</li>
                <li>🌍 Audience across 7+ countries</li>
                <li>🩺 Aspiring neuro-cardio surgeon</li>
              </ul>
            </div>
          </aside>
        </div>
      </article>

      {/* === PAGE 3: AUDIENCE & REACH === */}
      <article className="card-flat bg-white mb-6" style={{ border: "3px solid var(--ink)", boxShadow: "5px 5px 0 0 var(--ink)" }} data-testid="press-page-reach">
        <div className="eyebrow text-[var(--orange)] mb-1">PAGE 03 &middot; AUDIENCE &amp; REACH</div>
        <h3 className="font-display text-2xl sm:text-3xl text-[var(--ink)] mb-5 leading-tight">2M+ followers across 5 platforms</h3>

        <div className="grid md:grid-cols-2 gap-5">
          {/* Reach table */}
          <div>
            <div className="eyebrow text-[var(--red)] mb-2">SOCIAL REACH</div>
            <div className="rounded-xl" style={{ border: "2.5px solid var(--ink)", overflow: "hidden" }} data-testid="press-reach-table">
              {REACH.map((r, idx) => (
                <div key={r.platform} className="grid grid-cols-3 items-center px-3 py-2.5" style={{ background: idx % 2 ? "var(--paper-2)" : "#fff", borderBottom: "2px dashed #d8c890" }}>
                  <div className="font-fun text-sm text-[var(--ink)]">{r.emoji} {r.platform}</div>
                  <div className="text-xs text-[var(--ink-soft)] font-mono truncate">{r.handle}</div>
                  <div className="stat-num text-base sm:text-lg text-[var(--red)] text-right">{r.count}</div>
                </div>
              ))}
              <div className="grid grid-cols-3 items-center px-3 py-3" style={{ background: "var(--ink)" }}>
                <div className="font-display text-base sm:text-lg text-white">TOTAL REACH</div>
                <div></div>
                <div className="stat-num text-xl sm:text-2xl text-[var(--sun)] text-right">2M+</div>
              </div>
            </div>
          </div>

          {/* Facebook growth + geography */}
          <div>
            <div className="eyebrow text-[var(--red)] mb-2">FACEBOOK GROWTH</div>
            <div className="grid grid-cols-2 gap-2 mb-5" data-testid="press-growth">
              {GROWTH_STATS.map((g) => (
                <div key={g.label} className="card-flat bg-white" style={{ padding: "0.6rem 0.7rem" }}>
                  <div className="stat-num text-lg text-[var(--red)] leading-tight">{g.value}</div>
                  <div className="label-tag mt-0.5 leading-tight">{g.label}</div>
                </div>
              ))}
            </div>
            <div className="eyebrow text-[var(--red)] mb-2">AUDIENCE GEOGRAPHY</div>
            <div className="flex flex-wrap gap-1.5" data-testid="press-markets">
              {MARKETS.map((m) => (
                <span key={m} className="pill" style={{ background: "var(--paper-2)", color: "var(--ink)", fontSize: "0.72rem", border: "2px solid var(--ink)" }}>{m}</span>
              ))}
            </div>
            <p className="text-xs text-[var(--ink-soft)] mt-3 italic">Primary markets — audience also extends across the EU, Australia, and South Asia.</p>
          </div>
        </div>
      </article>

      {/* === PAGE 4: MEDIA FEATURES === */}
      <article className="card-flat bg-white mb-6" style={{ border: "3px solid var(--ink)", boxShadow: "5px 5px 0 0 var(--ink)" }} data-testid="press-page-features">
        <div className="eyebrow text-[var(--orange)] mb-1">PAGE 04 &middot; MEDIA FEATURES</div>
        <h3 className="font-display text-2xl sm:text-3xl text-[var(--ink)] mb-5 leading-tight">National media &amp; podcast features</h3>
        <div className="space-y-3" data-testid="press-features">
          {FEATURES.map((f) => (
            <div key={f.outlet} className="card-flat tint-cream" data-testid={`press-feature-${f.tier}`} style={{ padding: "0.9rem 1rem" }}>
              <div className="flex items-start gap-3 flex-wrap">
                <span className="pill" style={{ background: f.tier === "national" ? "var(--red)" : f.tier === "global" ? "var(--cyan)" : "var(--orange)", color: f.tier === "global" ? "var(--ink)" : "#fff", fontSize: "0.62rem", letterSpacing: "0.15em" }}>
                  {f.tier === "national" ? "🇺🇸 NATIONAL" : f.tier === "global" ? "🌍 GLOBAL" : "🎙 PODCAST"}
                </span>
                <div className="font-display text-lg sm:text-xl text-[var(--ink)] leading-tight flex-1">{f.outlet}</div>
              </div>
              <p className="text-[var(--ink-soft)] text-sm leading-relaxed mt-2">{f.blurb}</p>
            </div>
          ))}
        </div>
      </article>

      {/* === BOOK / PRESS CONTACT FOOTER === */}
      <article className="card-flat bg-white" style={{ border: "3px solid var(--ink)", boxShadow: "5px 5px 0 0 var(--ink)", overflow: "hidden", padding: 0 }} data-testid="press-page-contact">
        <div className="grid lg:grid-cols-2 gap-0">
          <div className="px-5 sm:px-8 py-6" style={{ background: "var(--ink)", color: "#fff" }}>
            <div className="eyebrow mb-2" style={{ color: "var(--sun)" }}>BOOK SEAN</div>
            <div className="font-display text-2xl sm:text-3xl leading-tight mb-2">
              Bring science class to life.
            </div>
            <p className="text-sm leading-relaxed mb-4" style={{ color: "rgba(255,255,255,0.78)" }}>
              Speaking engagements &middot; school assemblies &middot; brand partnerships &middot; podcast guest appearances &middot; conferences.
            </p>
            <a
              href="#bookings"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("bookings")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="btn btn-red"
              data-testid="press-book-cta"
            >
              🎤 Open booking form →
            </a>
          </div>
          <div className="px-5 sm:px-8 py-6" style={{ background: "var(--paper-2)" }}>
            <div className="eyebrow text-[var(--orange)] mb-2">PRESS CONTACT</div>
            <div className="font-display text-xl text-[var(--ink)] leading-tight mb-1">Sean's Team</div>
            <div className="space-y-1.5 text-sm text-[var(--ink)] mt-3 leading-relaxed">
              <div>📧 <a href="mailto:info@seanthesciencekid.net" className="text-[var(--red)] font-bold no-underline hover:underline" data-testid="press-email">info@seanthesciencekid.net</a></div>
              <div>🌐 <a href="https://seanthesciencekidofficial.com/" target="_blank" rel="noreferrer noopener" className="text-[var(--red)] font-bold no-underline hover:underline">seanthesciencekidofficial.com</a></div>
              <div>📸 <a href="https://www.instagram.com/seanthesciencekidofficial?rpxt=MWw2NjJjeXR2bW55dg%3D%3D&utm_source=qr" target="_blank" rel="noreferrer noopener" className="text-[var(--red)] font-bold no-underline hover:underline" data-testid="press-instagram">@seanthesciencekidofficial</a></div>
            </div>
            <div className="mt-4 pt-3" style={{ borderTop: "2px dashed #d8c890" }}>
              <div className="font-display text-base text-[var(--ink)]">Keep On Sciencing! 🧡⚡</div>
              <div className="label-tag mt-1">// sean the science kid · official media kit · 2026</div>
            </div>
          </div>
        </div>
      </article>
    </section>
  );
}
